import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Loader2, KeyRound, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BG_IMAGE = "/hero-cis.jpg";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Introduce email y contraseña");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      login(res.data, res.data.session_token);
    } catch (err) {
      const detail = err.response?.data?.detail || "Error de autenticación";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Introduce tu email");
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Email enviado si la cuenta existe");
    } catch {
      toast.error("Error al enviar email");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div />
          <div className="text-white">
            <img src="/valuo-horizontal.png" alt="Valuo.pro" className="h-14 w-auto object-contain mb-6" style={{ filter: "brightness(0) invert(1)" }} />
            <p className="text-white/70 text-lg max-w-md">
              Plataforma de análisis financiero<br />para el sector de agencias españolas
            </p>
          </div>
          <p className="text-white/50 text-xs">
            &copy; {new Date().getFullYear()} Valuo.pro — Es una marca registrada de BUD Advisors
          </p>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 bg-background">
        <div className="max-w-sm w-full mx-auto">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <img src="/logo_login.png" alt="Valuo.pro" className="h-10 w-auto object-contain" />
            <div className="w-12 h-1 mt-2" style={{ background: "#E1C422" }} />
          </div>

          {!forgotMode ? (
            <>
              <div className="mb-8">
                <img src="/valuo-favicon.png" alt="" className="w-8 h-8 mb-4 hidden lg:block" />
                <h2 className="font-serif text-3xl text-foreground mb-2" data-testid="login-title">
                  Bienvenido
                </h2>
                <p className="text-muted-foreground text-sm">
                  Accede con tus credenciales para continuar
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="mt-1 rounded-sm"
                    autoComplete="email"
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs">Contraseña</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Tu contraseña"
                      className="rounded-sm pr-10"
                      autoComplete="current-password"
                      data-testid="login-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1} data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-sm font-medium"
                  style={{ background: "#1a1a1a" }}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <LogIn className="w-4 h-4 mr-2" />
                  )}
                  Iniciar sesión
                </Button>
              </form>

              <button
                onClick={() => { setForgotMode(true); setForgotEmail(email); }}
                className="mt-4 text-xs hover:underline w-full text-center block"
                style={{ color: "#E1C422" }}
                data-testid="forgot-password-link"
              >
                <KeyRound className="w-3 h-3 inline mr-1" />
                He olvidado mi contraseña
              </button>

              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  Acceso restringido. Solo usuarios autorizados por Valuo.pro
                </p>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => { setForgotMode(false); setForgotSent(false); }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6 transition-colors"
                data-testid="back-to-login"
              >
                <ArrowLeft className="w-3 h-3" /> Volver al login
              </button>

              <div className="mb-6">
                <div className="w-10 h-1 mb-4" style={{ background: "#E1C422" }} />
                <h2 className="font-serif text-2xl text-foreground mb-2">
                  Recuperar contraseña
                </h2>
                <p className="text-muted-foreground text-sm">
                  Introduce tu email y te enviaremos un enlace para restablecer tu contraseña.
                </p>
              </div>

              {!forgotSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="forgotEmail" className="text-xs">Email</Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="mt-1 rounded-sm"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full h-11 rounded-sm font-medium"
                    style={{ background: "#1a1a1a" }}
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Enviar enlace de recuperación
                  </Button>
                </form>
              ) : (
                <div className="p-4 rounded-sm border text-center" style={{ borderColor: "#E1C422", background: "rgba(225,196,34,0.05)" }}>
                  <p className="text-sm font-medium mb-1">Email enviado</p>
                  <p className="text-xs text-muted-foreground">
                    Si existe una cuenta con ese email, recibirás un enlace para restablecer tu contraseña.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
